import React from 'react';
import Table from '../Table';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Delete, Edit } from '@mui/icons-material';
import { IListContentItem } from '../../models/post.model';
import { v4 as uuid } from 'uuid';
import { RichEditor } from '../RichEditor';
import { convertToRaw, EditorState } from 'draft-js';
import { createStateFromHtml } from '../../utils/draft-js';
import draftToHtml from 'draftjs-to-html';
import { ActionsContainer, Container } from './style';

type Props = {
  initData: IListContentItem[];
};

type State = {
  data: IListContentItem[];
  deletingItem?: IListContentItem;
  editingItem?: IListContentItem;
  nameContentState: EditorState;
  locationContentState: EditorState;
};

export class ListIncubatorInput extends React.Component<Props, State> {
  constructor(props: Readonly<Props>) {
    super(props);

    this.state = {
      data: props.initData,
      nameContentState: EditorState.createEmpty(),
      locationContentState: EditorState.createEmpty(),
    };
  }

  updateData = (data: IListContentItem[]) => {
    this.setState({
      data,
      deletingItem: undefined,
      editingItem: undefined,
      nameContentState: EditorState.createEmpty(),
      locationContentState: EditorState.createEmpty(),
    });
  };

  getData = () => {
    return this.state.data;
  };

  private _setEditingItem = (item?: IListContentItem) => {
    this.setState({
      nameContentState: createStateFromHtml(item?.name || ''),
      locationContentState: createStateFromHtml(item?.location || ''),
      editingItem: item,
    });
  };

  private _insertItem = (position: number) => {
    this.setState((prevState) => {
      const newArray = prevState.data.slice(0, position);
      newArray.push(
        {
          id: uuid(),
          location: '',
          name: '',
          order: -1,
        },
        ...prevState.data.slice(position),
      );

      this._correctOrder(newArray, position);

      return {
        data: newArray,
        editingItem: newArray[position],
        nameContentState: EditorState.createEmpty(),
        locationContentState: EditorState.createEmpty(),
      };
    });
  };

  private _findHeader(arr: IListContentItem[], index: number) {
    if (index < 1 || arr.length <= 0) {
      return -1;
    }

    for (let i = index - 1; i >= 0; i--) {
      if (arr[i]?.header) {
        return i;
      }
    }

    return -1;
  }

  private _correctOrder(arr: IListContentItem[], fromIndex: number): void {
    if (fromIndex >= arr.length) {
      return;
    }

    if (!arr[fromIndex]) {
      return;
    }

    if (arr[fromIndex].header) {
      arr[fromIndex] = {
        ...arr[fromIndex],
        order: -1,
      };
      return this._correctOrder(arr, fromIndex + 1);
    }

    const headerIndex = this._findHeader(arr, fromIndex);
    arr[fromIndex] = {
      ...arr[fromIndex],
      order: fromIndex - headerIndex,
    };

    for (let i = 1; i < arr.length && !arr[i + fromIndex]?.header; i++) {
      const idx = fromIndex + i;
      if (arr[idx]) {
        arr[idx] = {
          ...arr[idx],
          order: arr[fromIndex].order + i,
        };
      }
    }
  }

  private _handleUpdateItem = () => {
    if (!this.state.editingItem) {
      return;
    }
    const idx = this.state.data.findIndex(
      (row) => row.id === this.state.editingItem?.id,
    );
    if (idx >= 0) {
      this.setState((prevState) => {
        const newData = Array.from(prevState.data);
        newData[idx] = {
          ...prevState.editingItem!,
          name: draftToHtml(
            convertToRaw(prevState.nameContentState.getCurrentContent()),
          ),
          location: draftToHtml(
            convertToRaw(prevState.locationContentState.getCurrentContent()),
          ),
        };

        if (!prevState.data[idx].header !== !newData[idx].header) {
          this._correctOrder(newData, idx);
        }

        return {
          data: newData,
          editingItem: undefined,
          nameContentState: EditorState.createEmpty(),
          locationContentState: EditorState.createEmpty(),
        };
      });
    }
  };

  render(): React.ReactNode {
    return (
      <>
        <Container item xs={12}>
          <Table
            rowKey={(row) => row.id}
            rowClassName={(row) => (row?.header ? 'header-row' : undefined)}
            dataSource={this.state.data}
            columns={[
              {
                dataKey: '',
                title: 'No.',
                key: 'order',
                render: (data, idx) => (data.header ? '' : data.order),
              },
              {
                dataKey: 'name',
                key: 'name',
                title: 'Name',
                render: (data) =>
                  data?.header ? (
                    ''
                  ) : (
                    <div
                      dangerouslySetInnerHTML={{
                        __html: data.name,
                      }}
                    />
                  ),
              },
              {
                dataKey: 'header',
                key: 'header',
                title: 'Is header',
                render: (item) => (
                  <Checkbox
                    checked={!!item.header}
                    readOnly
                    disableRipple
                    sx={{ cursor: 'default' }}
                  />
                ),
              },
              {
                dataKey: 'location',
                key: 'location',
                title: 'Location',
                render: (data) =>
                  data?.header ? (
                    ''
                  ) : (
                    <div
                      dangerouslySetInnerHTML={{
                        __html: data.location,
                      }}
                    />
                  ),
              },
              {
                dataKey: '',
                title: 'Action',
                key: 'action',
                width: 150,
                render: (data, index) => {
                  return (
                    <ActionsContainer>
                      <Button
                        startIcon={<Add />}
                        variant="outlined"
                        size="small"
                        onClick={() => this._insertItem(index)}
                      >
                        Insert row above
                      </Button>
                      <Button
                        startIcon={<Add />}
                        variant="outlined"
                        size="small"
                        onClick={() => this._insertItem(index + 1)}
                      >
                        Insert row below
                      </Button>
                      <Button
                        startIcon={<Edit />}
                        variant="outlined"
                        color="secondary"
                        size="small"
                        onClick={() => this._setEditingItem(data)}
                      >
                        Edit
                      </Button>
                      <Button
                        startIcon={<Delete />}
                        variant="outlined"
                        size="small"
                        color="error"
                        onClick={() => this.setState({ deletingItem: data })}
                      >
                        Delete
                      </Button>
                    </ActionsContainer>
                  );
                },
              },
            ]}
          />
          <div style={{ padding: 8, textAlign: 'center' }}>
            {!this.state.data.length && (
              <Button
                startIcon={<Add />}
                variant="contained"
                onClick={() => {
                  this._insertItem(this.state.data.length);
                }}
              >
                Add row
              </Button>
            )}
          </div>
        </Container>
        <Dialog
          open={!!this.state.deletingItem}
          onClose={() => this.setState({ deletingItem: undefined })}
        >
          <DialogContent>
            <DialogContentText color={'black'}>
              This action cannot be revert. Do you want to delete this row?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              autoFocus
              onClick={() => this.setState({ deletingItem: undefined })}
              size="small"
            >
              Cancel
            </Button>
            <Button
              size="small"
              onClick={() => {
                if (this.state.deletingItem) {
                  this.setState((prevState) => {
                    const newArr = prevState.data.filter(
                      (row) => row.id !== this.state.deletingItem?.id,
                    );

                    this._correctOrder(
                      newArr,
                      prevState.data.findIndex(
                        (row) => row.id !== this.state.deletingItem?.id,
                      ),
                    );

                    return {
                      data: newArr,
                      deletingItem: undefined,
                    };
                  });
                }
              }}
              color="error"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={!!this.state.editingItem}
          onClose={() => this._setEditingItem()}
          fullWidth
          maxWidth="lg"
        >
          <DialogTitle>Update data</DialogTitle>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column' }}>
            <Grid container sx={{ mt: 4 }}>
              <Grid item xs={12}>
                <Typography variant="h5" p={1}>
                  Name
                </Typography>
              </Grid>
              <Grid
                item
                xs={12}
                sx={{ border: '1px solid rgba(0,0,0,0.1)', p: 1 }}
              >
                <RichEditor
                  contentState={this.state.nameContentState}
                  onEditorStateChange={(editorState) =>
                    this.setState({ nameContentState: editorState })
                  }
                />
              </Grid>
              <Grid item xs={12} sx={{ mt: 4 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!this.state.editingItem?.header}
                      onChange={(e, checked) => {
                        if (this.state.editingItem) {
                          this.setState((prevState) => {
                            return {
                              editingItem: {
                                ...prevState.editingItem!,
                                header: checked,
                              },
                            };
                          });
                        }
                      }}
                    />
                  }
                  label="Make this row as header"
                />
              </Grid>
              {!this.state.editingItem ||
              this.state.editingItem?.header ? null : (
                <>
                  <Grid item xs={12} sx={{ mt: 4 }}>
                    <Typography variant="h5" p={1}>
                      Location
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sx={{ border: '1px solid rgba(0,0,0,0.1)', p: 1 }}
                  >
                    <RichEditor
                      contentState={this.state.locationContentState}
                      onEditorStateChange={(editorState) =>
                        this.setState({ locationContentState: editorState })
                      }
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button
              autoFocus
              onClick={() => this._setEditingItem(undefined)}
              size="small"
            >
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={this._handleUpdateItem}
              color="primary"
            >
              Update
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }
}
